import React, { Fragment, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import ReactTooltip from 'react-tooltip';

import WebGalleryFavoritePhotosToolbar from '../WebGalleryFavoritePhotosToolbar';
import { prependAccountSlug } from '../../../../util/account';
import WebGalleryFavoritePhotosNullState from '../WebGalleryFavoritePhotosNullState';
import WebGalleryPhotoCard from '../WebGalleryPhotoCard';
import PhotoGrid from '../../../../components/grid/PhotoGrid';
import WebGalleryCollageUpsellModal from '../custom-prints/WebGalleryCollageUpsellModal';
import DismissiblePersistent from '../../../../components/global/DismissiblePersistent';
import { WG_COLLAGE_UPSELL_MODAL } from '../../../../constants/dismissibleKeys';
import {
  ANALYTIC_EVENTS,
  trackWebGalleryEvent,
} from '../../utilities/analytics';
import { getFavoritePhotoByPhotoId } from '../../../../util/favorites';
import { IMAGE_SIZE_CODE } from '../../../../constants/image-size-codes';
import WebGalleryPhotoViewerActions from '../WebGalleryPhotoViewerActions';
import ProgressivePhoto from '../../../../components/grid/ProgressivePhoto';
import WebGalleryDownloadModal from '../WebGalleryDownloadModal';
import PhotoGridViewerFooter from '../../../pro/views/Album/AlbumPhotos/components/PhotoGridViewerFooter';
import { PurchasingOptionsModal } from '../../../pro/views/HierarchyItem/Matches/components/PurchasingOptions';
import { PhotoSelectorContext } from '../../../pro/views/HierarchyItem/components/PhotoSelectorContext';
import PhotoSelectorHeader from '../../../pro/views/HierarchyItem/components/PhotoSelectorHeader';
import { isEntitledFreeWebGallery } from './helpers';
import { FAVORITED_PHOTOS_FILTER } from '../../../../constants/photos';
import { PHOTO_DOWNLOAD_MODAL } from '../../../../components/global/PhotoDownload';
import PhotoDownloadModal from '../../../../components/global/PhotoDownload/modal';
import useCloseModal from '../../../../hooks/useCloseModal';
import ShiftSelect from '../../../../components/global/ShiftSelect';
import PhotoSelectorToolbar from '../../../pro/views/HierarchyItem/components/PhotoSelectorToolbar';
import useAlbumFolderForWebGalleryPurchasedPhotoDownloads from './hooks/useAlbumFolderForWebGalleryPurchasedPhotoDownloads';

const propTypes = {
  loading: PropTypes.bool,
  folder: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
  }),
  album: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
  }),
  albumCustomPrint: PropTypes.shape({
    id: PropTypes.string,
    album: PropTypes.shape({
      id: PropTypes.string,
    }),
  }),
  favoritePhotos: PropTypes.shape({
    total: PropTypes.number,
    count: PropTypes.number,
    records: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        photo: PropTypes.shape({
          id: PropTypes.string,
          thumbnail_urls: PropTypes.arrayOf(
            PropTypes.shape({
              url: PropTypes.string,
              width: PropTypes.number,
              height: PropTypes.number,
            }),
          ),
          watermarked_urls: PropTypes.arrayOf(
            PropTypes.shape({
              url: PropTypes.string,
              width: PropTypes.number,
              height: PropTypes.number,
            }),
          ),
        }),
      }),
    ),
  }),
  client: PropTypes.shape({
    query: PropTypes.func.isRequired,
  }).isRequired,
  history: PropTypes.shape({
    length: PropTypes.number.isRequired,
    goBack: PropTypes.func.isRequired,
    push: PropTypes.func.isRequired,
  }),
  forceAllowHiResDownloads: PropTypes.bool,
};

const WebGalleryFavoritePhotos = ({
  loading,
  favoritePhotos,
  folder,
  album,
  history,
  albumCustomPrint,
  forceAllowHiResDownloads,
}) => {
  const {
    selectedPhotos,
    selectionSettings,
    selectionEnabled,
    onSelectPhotos,
    onDeselectPhotos,
    onResetSelection,
  } = useContext(PhotoSelectorContext);
  const closeModal = useCloseModal();
  const { isSelectAll } = selectionSettings;
  const albumFolderEntity = folder ?? album?.root_album_folder;
  const {
    purchasedPhotoDownloads,
  } = useAlbumFolderForWebGalleryPurchasedPhotoDownloads({
    albumFolderId: albumFolderEntity?.id,
    isFreeWebGallery: albumFolderEntity?.is_web_gallery,
  });
  const isEntitledFreeWebGalleryBool = isEntitledFreeWebGallery(
    folder ?? album?.root_album_folder,
  );
  const favoritePhotosRecords = favoritePhotos?.records ?? [];
  const totalPhotos = favoritePhotosRecords.length;
  const entitledFavoritePhotos = useMemo(() => {
    if (isEntitledFreeWebGalleryBool) {
      return favoritePhotosRecords;
    }

    return favoritePhotosRecords.filter(({ photo }) =>
      purchasedPhotoDownloads.includes(photo.id),
    );
  }, [favoritePhotosRecords, purchasedPhotoDownloads, albumFolderEntity]);

  const totalSelectedPhotos = (() => {
    return isSelectAll
      ? entitledFavoritePhotos.length - selectedPhotos.length
      : selectedPhotos.length;
  })();

  if ((loading && !favoritePhotos) || (!folder && !album)) {
    return (
      <div className="d-block text-center mt-6">
        <span className="spinner" />
      </div>
    );
  }

  const homeLink = folder
    ? prependAccountSlug(`/galleries/view/folder/${folder.id}`, folder)
    : prependAccountSlug(`/galleries/view/album/${album.id}`, album);

  const albumCustomPrintPhotosNo = albumCustomPrint?.custom_print?.photos_no;

  return (
    <Fragment>
      <WebGalleryFavoritePhotosToolbar folder={folder} album={album} />

      {favoritePhotosRecords.length > 0 ? (
        <div className="wg-photo-grid-listing">
          <PhotoSelectorHeader
            totalPhotos={totalPhotos}
            className="pr-1 pr-md-0"
          />
          <ShiftSelect
            items={(favoritePhotos?.records ?? [])?.map(
              ({ photo }) => photo.id,
            )}
            onSelectItems={onSelectPhotos}
            onDeselectItems={onDeselectPhotos}
          >
            {({ onToggleSelectedItem: onToggleSelectedPhoto }) => (
              <PhotoGrid
                records={favoritePhotos.records}
                recordPhotoAccessor="photo"
                nativePhotoViewer
                photoViewerProps={{
                  forceActionsDropdown: true,
                  actions: ({
                    record: favoritePhoto,
                    onNavigateNextAfterRemoval,
                  }) => {
                    const {
                      photo,
                      album: favoritePhotoAlbum = album,
                    } = favoritePhoto;

                    return (
                      <WebGalleryPhotoViewerActions
                        album={favoritePhotoAlbum}
                        photo={photo}
                        onFavoriteToggled={async () => {
                          onNavigateNextAfterRemoval();
                        }}
                        favoritePhoto={getFavoritePhotoByPhotoId(
                          favoritePhotosRecords,
                          photo.id,
                        )}
                        forceAllowHiResDownloads={forceAllowHiResDownloads}
                      />
                    );
                  },
                  renderPhoto: ({ photo, record }) => {
                    return (
                      <ProgressivePhoto
                        key={record.id}
                        id={record.id}
                        thumbnails={
                          photo.watermarked_urls || photo.thumbnail_urls
                        }
                        size={
                          forceAllowHiResDownloads
                            ? IMAGE_SIZE_CODE.LARGE1X
                            : IMAGE_SIZE_CODE.LARGE
                        }
                        allowFallbackThumbnail
                      />
                    );
                  },
                  footer: ({ record }) => (
                    <PhotoGridViewerFooter
                      entity={record?.album ?? album}
                      photo={record.photo}
                      isFreeWebGallery
                    />
                  ),
                }}
                cellRenderer={({ record: favoritePhoto, recordPath }) => {
                  const { photo } = favoritePhoto;
                  const isEntitled =
                    isEntitledFreeWebGalleryBool ||
                    purchasedPhotoDownloads.includes(photo.id);

                  return (
                    <WebGalleryPhotoCard
                      id={photo.id}
                      linkTo={recordPath}
                      thumbnails={
                        photo.watermarked_urls || photo.thumbnail_urls
                      }
                      useCheckbox={isEntitled}
                      selectable={selectionEnabled}
                      isSelected={(() => {
                        if (!isEntitled) {
                          return false;
                        }

                        return isSelectAll
                          ? !selectedPhotos.includes(photo.id)
                          : selectedPhotos.includes(photo.id);
                      })()}
                      onSelect={e => {
                        e.stopPropagation();
                        if (!isEntitled) {
                          return;
                        }

                        onToggleSelectedPhoto(
                          photo.id,
                          selectedPhotos.includes(photo.id),
                        );
                      }}
                      selectableContent={
                        isEntitled ? undefined : (
                          <div
                            className="web-gallery-photo-card-unselectable-overlay"
                            data-testid="matched-photo-selection-waldo-face-block"
                            data-tip="Purchase photo first to download"
                            data-for={`select-unentitled-photo-tip-${photo.id}`}
                            data-effect="solid"
                            data-place="bottom"
                          >
                            <ReactTooltip
                              id={`select-unentitled-photo-tip-${photo.id}`}
                            />
                          </div>
                        )
                      }
                      progressive={false}
                      size={
                        forceAllowHiResDownloads
                          ? IMAGE_SIZE_CODE.LARGE1X
                          : IMAGE_SIZE_CODE.MEDIUM2X
                      }
                    />
                  );
                }}
              />
            )}
          </ShiftSelect>
          <PhotoSelectorToolbar totalSelectedPhotos={totalSelectedPhotos} />
          {albumCustomPrint &&
            (favoritePhotos?.records?.length ?? 0) >=
              albumCustomPrintPhotosNo && (
              <DismissiblePersistent storeKey={WG_COLLAGE_UPSELL_MODAL}>
                {(onDismiss, visible) =>
                  visible && (
                    <WebGalleryCollageUpsellModal
                      forceOpen
                      albumCustomPrint={albumCustomPrint}
                      onDismiss={onDismiss}
                      photos={favoritePhotos.records.map(fav => fav.photo)}
                      shuffle
                      onConfirm={() => {
                        trackWebGalleryEvent(
                          ANALYTIC_EVENTS.COLLAGE_PITCH_VIEW_BUY_TAPPED,
                          { position: 'favorites' },
                        );
                      }}
                      onMount={() => {
                        trackWebGalleryEvent(
                          ANALYTIC_EVENTS.COLLAGE_PITCH_VIEW_SHOW,
                          { position: 'favorites' },
                        );
                      }}
                    />
                  )
                }
              </DismissiblePersistent>
            )}
        </div>
      ) : (
        <WebGalleryFavoritePhotosNullState link={homeLink} history={history} />
      )}
      <WebGalleryDownloadModal />
      <PurchasingOptionsModal />
      <PhotoDownloadModal
        totalPhotos={totalSelectedPhotos}
        photoDownloadCreateInput={{
          photo_ids: {
            select_all: isSelectAll,
            selected_ids: isSelectAll ? [] : selectedPhotos,
            ignored_ids: isSelectAll ? selectedPhotos : [],
          },
          album: album
            ? {
                album_id: album.id,
              }
            : undefined,
          album_folder: folder
            ? {
                album_folder_id: folder.id,
              }
            : undefined,
          favorited_photos_only: true,
          favorited_by: FAVORITED_PHOTOS_FILTER.OWNED,
        }}
        onClose={() => {
          onResetSelection();
          closeModal(PHOTO_DOWNLOAD_MODAL);
        }}
      />
    </Fragment>
  );
};

WebGalleryFavoritePhotos.propTypes = propTypes;

export default WebGalleryFavoritePhotos;
